import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Clock, Euro, Play, Image as ImageIcon } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useParams } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Portfolio() {
  const { slug } = useParams();  // Récupérer le slug depuis l'URL
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMedia, setSelectedMedia] = useState(null);

  useEffect(() => {
    if (slug) {
      fetchPortfolio();
    } else {
      setError("Portfolio non trouvé");
      setLoading(false);
    }
  }, [slug]);

  const fetchPortfolio = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/portfolio/${slug}/services`);
      setServices(response.data);
    } catch (error) {
      console.error('Erreur chargement portfolio:', error);
      setError(error.response?.status === 404 ? "Portfolio non trouvé" : "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  };

  const openMediaViewer = (media) => {
    setSelectedMedia(media);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-lg text-muted-foreground">Chargement...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-lg font-semibold mb-2">{error}</p>
            <p className="text-muted-foreground text-center">Ce portfolio n'existe pas ou n'est plus accessible.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="container mx-auto px-4 py-8">
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight text-center">
            Portfolio
          </h1>
          <p className="text-center text-muted-foreground mt-3 text-base sm:text-lg">
            Découvrez mes réalisations
          </p>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-8">
        {services.length === 0 ? (
          <Card className="rounded-xl border-border/60">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <ImageIcon className="w-20 h-20 text-muted-foreground mb-4" strokeWidth={1} />
              <p className="text-muted-foreground text-lg">Aucune réalisation pour le moment</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-12">
            {services.map((service) => (
              <section key={service.id} className="space-y-6">
                <div className="flex items-center justify-between flex-wrap gap-4">
                  <div>
                    <h2 className="text-3xl font-bold">{service.nom}</h2>
                    <div className="flex items-center gap-4 mt-2 text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        <span className="text-sm">{service.duree} min</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Euro className="w-4 h-4" />
                        <span className="text-sm">{service.prix.toFixed(2)} €</span>
                      </div>
                    </div>
                  </div>
                  <Badge variant="outline" className="text-sm">
                    {service.media?.length || 0} photo{service.media?.length > 1 ? 's' : ''}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {service.media?.map((media, index) => (
                    <Card
                      key={index}
                      className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow group"
                      onClick={() => openMediaViewer(media)}
                    >
                      <div className="aspect-square relative">
                        {media.resource_type === 'video' ? (
                          <>
                            <video
                              src={media.url}
                              className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 flex items-center justify-center bg-black/40 group-hover:bg-black/50 transition-colors">
                              <Play className="w-12 h-12 text-white" />
                            </div>
                          </>
                        ) : (
                          <img
                            src={media.url}
                            alt={`${service.nom} - Photo ${index + 1}`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        )}
                      </div>
                    </Card>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}
      </main>

      {/* Media Viewer Dialog */}
      <Dialog open={!!selectedMedia} onOpenChange={() => setSelectedMedia(null)}>
        <DialogContent className="max-w-4xl w-full p-0">
          {selectedMedia && (
            <div className="relative bg-black rounded-lg overflow-hidden">
              {selectedMedia.resource_type === 'video' ? (
                <video
                  src={selectedMedia.url}
                  controls
                  autoPlay
                  className="w-full max-h-[80vh]"
                />
              ) : (
                <img
                  src={selectedMedia.url}
                  alt="Aperçu"
                  className="w-full max-h-[80vh] object-contain"
                />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Footer */}
      <footer className="border-t border-border mt-16">
        <div className="container mx-auto px-4 py-6 text-center text-muted-foreground text-sm">
          © 2025 AdminSAAS - Portfolio
        </div>
      </footer>
    </div>
  );
}
